import React, { useCallback, useEffect, useState } from "react";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import { useDispatch, useSelector } from "react-redux";
import { setUser } from "state";
import api from "utils/api";
import "styles/email-verification-popup.css";

const POLL_MS = 5000;

const EmailVerificationPopup = () => {
  const user = useSelector((s) => s.user);
  const token = useSelector((s) => s.token);
  const dispatch = useDispatch();
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  const open = Boolean(token && user && user.emailVerified === false);

  const refreshUser = useCallback(async () => {
    if (!open) return;
    try {
      const { data } = await api.get("/auth/me");
      if (data?.user) {
        dispatch(setUser({ user: data.user }));
      }
    } catch {
      // Keep the popup visible; auth interceptors / logout controls handle stale sessions.
    }
  }, [dispatch, open]);

  useEffect(() => {
    if (!open) {
      setSending(false);
      setStatus("");
      setError("");
      return undefined;
    }

    refreshUser();
    const intervalId = window.setInterval(refreshUser, POLL_MS);
    const onFocus = () => refreshUser();
    const onVisibilityChange = () => {
      if (!document.hidden) refreshUser();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [open, refreshUser]);

  const resendVerification = async () => {
    if (!user?.email || sending) return;
    setSending(true);
    setStatus("");
    setError("");
    try {
      await api.post("/auth/resend-verification", {
        email: user.email,
        brand: "dr-hoffmann",
      });
      setStatus("Wir haben dir einen neuen Bestätigungslink gesendet.");
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Die E-Mail konnte nicht gesendet werden. Bitte versuche es gleich erneut."
      );
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div className="email-verification-popup" role="presentation">
      <div className="email-verification-popup__backdrop" aria-hidden="true" />
      <div
        className="email-verification-popup__dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="email-verification-popup-title"
        aria-describedby="email-verification-popup-message"
      >
        <span className="email-verification-popup__mark" aria-hidden="true">
          <MarkEmailReadOutlinedIcon />
        </span>
        <p className="email-verification-popup__kicker">E-Mail bestätigen</p>
        <h2 id="email-verification-popup-title">Bitte bestätige deine E-Mail-Adresse</h2>
        <p id="email-verification-popup-message" className="email-verification-popup__message">
          Öffne den Link in deinem Postfach. Sobald deine E-Mail bestätigt ist,
          verschwindet dieses Fenster automatisch.
        </p>
        {user.email && <p className="email-verification-popup__email">{user.email}</p>}
        {status && <p className="email-verification-popup__status">{status}</p>}
        {error && <p className="email-verification-popup__error">{error}</p>}
        <button
          type="button"
          className="email-verification-popup__button"
          onClick={resendVerification}
          disabled={sending}
        >
          {sending ? "Wird gesendet..." : "Bestätigungs-E-Mail erneut senden"}
        </button>
      </div>
    </div>
  );
};

export default EmailVerificationPopup;
